import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'

import { Store } from '@/domain/store'
import { ServiceProduct } from '@/domain/service-product'
import { ServiceProductVariation } from '@/domain/service-product-variation'
import { StorageMigrationService } from '@/services/storage-migration.service'
import { SessionStateService, SessionState } from '@/services/session-state.service'
import { log, warn } from '@/util/logger'

export interface UIState {
  // POS State
  posSelectedStore: Store | null
  posSelectedProducts: any[]

  // Calendar State
  calendarSelectedStore: Store | null
  calendarSelectedProduct: ServiceProduct | null
  calendarSelectedVariation: ServiceProductVariation | null
}

/**
 * Centralized service for managing UI state across components.
 * This service preserves UI selections during token refreshes and provides
 * a single source of truth for UI state that should persist across
 * authentication updates.
 */
@Injectable({ providedIn: 'root' })
export class UIStateService {
  private readonly STORAGE_KEY = 'deux_ui'

  private _state = new BehaviorSubject<UIState>(this._loadState())
  public readonly state$ = this._state.asObservable()

  // Get current state synchronously
  get currentState(): UIState {
    return this._state.value
  }

  // Convenience observables for specific parts of state
  public readonly posStore$ = this.state$.pipe(
    map(state => state.posSelectedStore),
    distinctUntilChanged()
  )

  public readonly calendarStore$ = this.state$.pipe(
    map(state => state.calendarSelectedStore),
    distinctUntilChanged()
  )

  public readonly calendarProduct$ = this.state$.pipe(
    map(state => state.calendarSelectedProduct),
    distinctUntilChanged()
  )

  public readonly calendarVariation$ = this.state$.pipe(
    map(state => state.calendarSelectedVariation),
    distinctUntilChanged()
  )

  public readonly posProducts$ = this.state$.pipe(
    map(state => state.posSelectedProducts),
    distinctUntilChanged()
  )

  constructor(
    private _migration: StorageMigrationService,
    private _sessionState: SessionStateService
  ) {
    // Ensure migration has run (in case this service is initialized before AppComponent)
    // Migration is idempotent, so safe to call multiple times
    this._migration.migrateIfNeeded()
    
    // Sync state changes to sessionStorage
    this.state$.subscribe(state => {
      this._saveState(state)
    })
  }

  // POS State Management
  setPosStore(store: Store | null): void {
    // Don't update UI state if user is logged out or session expired
    const sessionState = this._sessionState.currentState
    if (sessionState === SessionState.LoggedOut || sessionState === SessionState.Expired) {
      warn('[UIState] Ignoring POS store update - session is logged out or expired', {
        sessionState,
        attemptedStore: store ? { id: store.id, name: store.name } : null
      })
      return
    }

    const previous = this._state.value.posSelectedStore

    // Deduplicate: skip if the store ID hasn't changed
    if ((store?.id === previous?.id) && (store?.id !== undefined)) {
      return
    }

    log('[UIState] Setting POS store', {
      previous: previous ? { id: previous.id, name: previous.name } : null,
      new: store ? { id: store.id, name: store.name } : null
    })
    this._updateState({ posSelectedStore: store })
  }

  setPosProducts(products: any[]): void {
    this._updateState({ posSelectedProducts: products })
  }

  // Calendar State Management
  setCalendarStore(store: Store | null): void {
    // Don't update UI state if user is logged out or session expired
    const sessionState = this._sessionState.currentState
    if (sessionState === SessionState.LoggedOut || sessionState === SessionState.Expired) {
      warn('[UIState] Ignoring Calendar store update - session is logged out or expired', {
        sessionState,
        attemptedStore: store ? { id: store.id, name: store.name } : null
      })
      return
    }

    const previous = this._state.value.calendarSelectedStore

    // Deduplicate: skip if the store ID hasn't changed
    if ((store?.id === previous?.id) && (store?.id !== undefined)) {
      return
    }

    log('[UIState] Setting Calendar store', {
      previous: previous ? { id: previous.id, name: previous.name } : null,
      new: store ? { id: store.id, name: store.name } : null
    })
    this._updateState({ calendarSelectedStore: store })
  }

  setCalendarProduct(product: ServiceProduct | null): void {
    // Don't update UI state if user is logged out or session expired
    const sessionState = this._sessionState.currentState
    if (sessionState === SessionState.LoggedOut || sessionState === SessionState.Expired) {
      warn('[UIState] Ignoring Calendar product update - session is logged out or expired', {
        sessionState,
        attemptedProduct: product ? { id: product.id } : null
      })
      return
    }

    const previous = this._state.value.calendarSelectedProduct

    // Deduplicate: skip if the product ID hasn't changed
    if ((product?.id === previous?.id) && (product?.id !== undefined)) {
      return
    }

    log('[UIState] Setting Calendar product', {
      previous: previous ? { id: previous.id } : null,
      new: product ? { id: product.id } : null
    })
    this._updateState({ calendarSelectedProduct: product })
  }

  setCalendarVariation(variation: ServiceProductVariation | null): void {
    // Don't update UI state if user is logged out or session expired
    const sessionState = this._sessionState.currentState
    if (sessionState === SessionState.LoggedOut || sessionState === SessionState.Expired) {
      warn('[UIState] Ignoring Calendar variation update - session is logged out or expired', {
        sessionState,
        attemptedVariation: variation ? { id: variation.id } : null
      })
      return
    }

    const previous = this._state.value.calendarSelectedVariation

    // Deduplicate: skip if the variation ID hasn't changed
    if ((variation?.id === previous?.id) && (variation?.id !== undefined)) {
      return
    }

    log('[UIState] Setting Calendar variation', {
      previous: previous ? { id: previous.id } : null,
      new: variation ? { id: variation.id } : null
    })
    this._updateState({ calendarSelectedVariation: variation })
  }

  // Clear all state (e.g., on logout)
  clearAll(): void {
    const previousState = this._state.value
    log('[UIState] Clearing all state', {
      hadPosStore: !!previousState.posSelectedStore,
      hadCalendarStore: !!previousState.calendarSelectedStore,
      hadCalendarProduct: !!previousState.calendarSelectedProduct,
      hadCalendarVariation: !!previousState.calendarSelectedVariation
    })
    const emptyState: UIState = {
      posSelectedStore: null,
      posSelectedProducts: [],
      calendarSelectedStore: null,
      calendarSelectedProduct: null,
      calendarSelectedVariation: null
    }
    this._state.next(emptyState)
    sessionStorage.removeItem(this.STORAGE_KEY)
  }

  // Clear POS-specific state
  clearPosState(): void {
    this._updateState({
      posSelectedStore: null,
      posSelectedProducts: []
    })
  }

  // Clear Calendar-specific state
  clearCalendarState(): void {
    this._updateState({
      calendarSelectedStore: null,
      calendarSelectedProduct: null,
      calendarSelectedVariation: null
    })
  }

  /**
   * Update stores array while preserving selections.
   * This is called when stores are refreshed from the server.
   */
  updateStoresPreservingSelections(newStores: Store[]): void {
    const currentState = this._state.value
    const updates: Partial<UIState> = {}
    const logData: any = {
      newStoreCount: newStores.length,
      timestamp: new Date().toISOString()
    }

    // Update POS store if it exists in new array
    if (currentState.posSelectedStore) {
      const previousId = currentState.posSelectedStore.id
      const updatedPosStore = newStores.find(s => s.id === previousId)
      if (updatedPosStore) {
        updates.posSelectedStore = updatedPosStore
        logData.posStore = { preserved: true, id: previousId, name: updatedPosStore.name }
      } else {
        updates.posSelectedStore = null
        logData.posStore = { preserved: false, id: previousId, reason: 'not found in new stores' }
      }
    }

    // Update Calendar store if it exists in new array
    if (currentState.calendarSelectedStore) {
      const previousId = currentState.calendarSelectedStore.id
      const updatedCalendarStore = newStores.find(s => s.id === previousId)
      if (updatedCalendarStore) {
        updates.calendarSelectedStore = updatedCalendarStore
        logData.calendarStore = { preserved: true, id: previousId, name: updatedCalendarStore.name }
      } else {
        updates.calendarSelectedStore = null
        logData.calendarStore = { preserved: false, id: previousId, reason: 'not found in new stores' }
      }
    }

    if (Object.keys(updates).length > 0) {
      log('[UIState] Updating stores while preserving selections', logData)
      this._updateState(updates)
    } else {
      log('[UIState] Stores updated, no selections to preserve', logData)
    }
  }

  private _updateState(updates: Partial<UIState>): void {
    const currentState = this._state.value
    this._state.next({ ...currentState, ...updates })
  }

  private _loadState(): UIState {
    const defaultState: UIState = {
      posSelectedStore: null,
      posSelectedProducts: [],
      calendarSelectedStore: null,
      calendarSelectedProduct: null,
      calendarSelectedVariation: null
    }

    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        
        // Validate parsed data structure
        if (!parsed || typeof parsed !== 'object') {
          warn('[UIState] Invalid state format in storage, using defaults')
          return defaultState
        }

        // Only restore IDs and simple data, not full objects
        // Components will need to resolve full objects from IDs
        // Merge with defaults to ensure all properties exist
        const restored = {
          ...defaultState,
          ...parsed
        }

        // Validate that restored state has correct structure
        // Convert partial objects (from migration) to null if they're not full objects
        if (restored.posSelectedStore && !restored.posSelectedStore.id) {
          restored.posSelectedStore = null
        }
        if (restored.calendarSelectedStore && !restored.calendarSelectedStore.id) {
          restored.calendarSelectedStore = null
        }
        if (restored.calendarSelectedProduct && !restored.calendarSelectedProduct.id) {
          restored.calendarSelectedProduct = null
        }
        if (restored.calendarSelectedVariation && !restored.calendarSelectedVariation.id) {
          restored.calendarSelectedVariation = null
        }

        return restored
      }
    } catch (e) {
      warn('[UIState] Failed to load state from storage, using defaults', e)
      // Clear corrupted data
      try {
        sessionStorage.removeItem(this.STORAGE_KEY)
      } catch (clearError) {
        // Ignore errors when clearing
      }
    }

    return defaultState
  }

  private _saveState(state: UIState): void {
    try {
      // Save only IDs and simple data to sessionStorage
      // For products, save minimal identifying info (seller_id + product number) to keep storage small
      const posProductsToSave = state.posSelectedProducts?.map(p => {
        if (p && p.sales_period && p.sales_period.seller_id && p.number) {
          return {
            seller_id: p.sales_period.seller_id,
            number: p.number,
            // Save pos_set_price even if it's 0 (use !== undefined check, not || operator)
            pos_set_price: p.pos_set_price !== undefined && p.pos_set_price !== null ? p.pos_set_price : null
          }
        }
        return null
      }).filter(p => p !== null) || []

      const toSave = {
        posSelectedStore: state.posSelectedStore ? { id: state.posSelectedStore.id, name: state.posSelectedStore.name } : null,
        posSelectedProducts: posProductsToSave,
        calendarSelectedStore: state.calendarSelectedStore ? { id: state.calendarSelectedStore.id, name: state.calendarSelectedStore.name } : null,
        calendarSelectedProduct: state.calendarSelectedProduct ? { id: state.calendarSelectedProduct.id } : null,
        calendarSelectedVariation: state.calendarSelectedVariation ? { id: state.calendarSelectedVariation.id } : null
      }
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(toSave))
      log('[UIState] Saved state to sessionStorage', {
        posStore: toSave.posSelectedStore ? { id: toSave.posSelectedStore.id } : null,
        posProductsCount: posProductsToSave.length,
        calendarStore: toSave.calendarSelectedStore ? { id: toSave.calendarSelectedStore.id } : null,
        calendarProduct: toSave.calendarSelectedProduct ? { id: toSave.calendarSelectedProduct.id } : null,
        calendarVariation: toSave.calendarSelectedVariation ? { id: toSave.calendarSelectedVariation.id } : null
      })
    } catch (e) {
      warn('[UIState] Failed to save state to storage', e)
    }
  }
}